import {
  useCachedMembers,
  useIsAdminOrManagerRole,
  useReviewTransactionAccountSummary,
  useWorkspaceSettings,
} from '@easy-expense/data-firestore-client';
import Data from '@easy-expense/frontend-data-layer';
import { getTranslation, useIntlStore } from '@easy-expense/intl-client';
import { Icon } from '@easy-expense/ui-shared-components';
import { theme } from '@easy-expense/ui-theme';
import { Layout, OpenSans, Spacer } from '@easy-expense/ui-web-core';
import { getCombinedExpenseStatus } from '@easy-expense/utils-client';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { ApprovalsPromoBanner } from './Approvals/ApprovalsPromoBanner.component';
import { BankTransactionHeader } from './BankTransactionHeader.component';

export const ExpenseActions: React.FC = () => {
  const navigate = useNavigate();
  return (
    <Layout.Row align>
      <OpenSans.Primary size={32} weight="bold-700">
        {getTranslation('Transactions')}
      </OpenSans.Primary>
      <Spacer.Horizontal size={32} />
      <Layout.PressableRow
        onClick={() => navigate('/expense')}
        bg="brandPrimaryXLight"
        border={[1, 'solid', 'brandPrimary']}
        py={4}
        px
        radius={100}
        align
      >
        <Icon name="add-outline" size={16} color={theme.colors.brandPrimary} />
        <Spacer.Horizontal size={8} />
        <OpenSans.Pressable>{getTranslation('Add Transaction')}</OpenSans.Pressable>
      </Layout.PressableRow>
    </Layout.Row>
  );
};

export const ReceiptOptions: React.FC = () => {
  const { forwardedEmailsTransactionCount } = useReviewTransactionAccountSummary({
    filterZeroCount: true,
  });

  const navigate = useNavigate();

  if (forwardedEmailsTransactionCount > 0) {
    return (
      <Layout.Row>
        <Layout.PressableRow
          px={16}
          py
          border={[1, 'solid', 'brandPrimary']}
          radius={8}
          align
          bg="brandPrimaryXLight"
          onClick={() => navigate('/review')}
        >
          <Icon name="mail-outline" size={24} color={theme.colors.brandPrimary} />
          <Spacer.Horizontal size={16} />
          <Layout.Column>
            <OpenSans.Primary weight="bold-700" size="s-16">
              {forwardedEmailsTransactionCount +
                ' ' +
                getTranslation('Email Receipts Ready for Review')}
            </OpenSans.Primary>
            <OpenSans.Primary size="xs-12">
              {getTranslation('Forward to: upload@easy-expense.com')}
            </OpenSans.Primary>
          </Layout.Column>
        </Layout.PressableRow>
      </Layout.Row>
    );
  }

  return (
    <Layout.Row>
      <Layout.Row px={16} py border={[1, 'solid', 'grayLight']} radius={8} align bg="white">
        <Icon name="mail-outline" size={24} color={theme.colors.gray} />
        <Spacer.Horizontal size={16} />
        <Layout.Column>
          <OpenSans.Primary weight="bold-700" size="s-16">
            {getTranslation('Import emailed receipts')}
          </OpenSans.Primary>
          <OpenSans.Primary size="xs-12">
            {__DEV__
              ? getTranslation('Forward to: upload+staging@easy-expense.com')
              : getTranslation('Forward to: upload@easy-expense.com')}
          </OpenSans.Primary>
        </Layout.Column>
        <Spacer.Horizontal size={8} />
      </Layout.Row>
    </Layout.Row>
  );
};

export const ApprovalBanner: React.FC = () => {
  const isManagerOrAdmin = useIsAdminOrManagerRole();
  const { formatCurrency } = useIntlStore();
  const navigate = useNavigate();
  const expenses = Data.expenses.use();
  const { workspaceSettings } = useWorkspaceSettings();
  const members = useCachedMembers();

  /** Solo workspaces have no one to approve or reimburse expenses, so the banner is noise. */
  const hasTeammates = members.length > 1;

  const requiresApproval = workspaceSettings?.requiresApproval ?? false;
  const showsReimbursements = workspaceSettings?.showReimbursementsToggle ?? false;

  // Both workflows off means there's nothing to review — an expense can still carry a stale
  // approvalStatus/isReimbursable from before the toggles were turned off, so gate on the
  // settings rather than on the expense data. Mirrors the approvalStatus column in
  // MaterialExpenseTable.component.tsx.
  const usesReviewWorkflow = requiresApproval || showsReimbursements;

  // Count only the workflows the workspace actually runs, so the tally always matches the label
  // below — otherwise a reimbursement-only workspace would count stale pending expenses under
  // copy that never mentions approval.
  const pendingExpenses = React.useMemo(
    () =>
      expenses.filter((expense) => {
        const status = getCombinedExpenseStatus(expense);
        if (status === 'pending') {
          return requiresApproval;
        }
        if (status === 'needsReimbursement') {
          return showsReimbursements;
        }
        return false;
      }),
    [expenses, requiresApproval, showsReimbursements],
  );

  // One raw string per toggle combination — the copy must never name a workflow the workspace
  // has turned off. Kept as full literals rather than composed fragments so translators get a
  // whole phrase (see the getTranslation rules in CLAUDE.md).
  function reviewLabel(count: number) {
    if (requiresApproval && showsReimbursements) {
      if (count === 1) {
        return getTranslation('expense requiring approval or reimbursement');
      }
      return getTranslation('expenses requiring approval or reimbursement');
    }
    if (requiresApproval) {
      if (count === 1) {
        return getTranslation('expense requiring approval');
      }
      return getTranslation('expenses requiring approval');
    }
    if (count === 1) {
      return getTranslation('expense requiring reimbursement');
    }
    return getTranslation('expenses requiring reimbursement');
  }

  if (!isManagerOrAdmin || !hasTeammates || !usesReviewWorkflow || pendingExpenses.length === 0) {
    return null;
  }

  const pendingTotal = pendingExpenses.reduce((sum, expense) => sum + expense.total, 0);
  const memberCount = new Set(pendingExpenses.map((expense) => expense.createdBy)).size;

  return (
    <Layout.PressableRow
      align
      px={16}
      py
      radius={8}
      bg="warningXLight"
      border={[1, 'solid', 'warning']}
      onClick={() => navigate('/approvals')}
      style={{ paddingBottom: 8 }}
    >
      <Layout.Row bg="warningDark" radius={8} px={10} py={10} align center>
        <Icon name="alert-circle-outline" size={20} color={theme.colors.buttonWhite} />
      </Layout.Row>
      <Spacer.Horizontal size={16} />
      <Layout.Column grow>
        <OpenSans.Primary weight="bold-700" size="s-16" style={{ color: theme.colors.warningDark }}>
          {pendingExpenses.length} {reviewLabel(pendingExpenses.length)}
        </OpenSans.Primary>
        <OpenSans.Secondary size="xs-12">
          {formatCurrency(pendingTotal)}{' '}
          {memberCount === 1
            ? getTranslation('from 1 team member')
            : `${getTranslation('from')} ${memberCount} ${getTranslation('team members')}`}
        </OpenSans.Secondary>
      </Layout.Column>
      <Layout.Row px={20} py={10} bg="warningDark" radius={8} align>
        <OpenSans.Primary weight="bold-700" size="s-16" style={{ color: theme.colors.buttonWhite }}>
          {getTranslation('Review')}
        </OpenSans.Primary>
      </Layout.Row>
    </Layout.PressableRow>
  );
};

export const TransactionListHeader: React.FC<object> = () => {
  return (
    <Layout.Column grow>
      <ExpenseActions />
      <Spacer.Vertical size={12} />
      <Layout.Row align>
        <ReceiptOptions />
        <Spacer.Horizontal />
        <BankTransactionHeader />
      </Layout.Row>
      <Spacer.Vertical size={12} />
      <ApprovalBanner />
      <ApprovalsPromoBanner />
    </Layout.Column>
  );
};
